import React, { useLayoutEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import type { SectionAnimation } from "@repo/backend/schema/siteConfig";
import { applyPreset } from "./presets.js";

gsap.registerPlugin(ScrollTrigger);

interface SectionAnimatorProps {
  animation?: SectionAnimation;
  sectionId?: string;
  children: React.ReactNode;
}

/**
 * Wraps a section and applies a GSAP ScrollTrigger animation when it enters
 * the viewport. useLayoutEffect ensures the initial "from" state (opacity 0,
 * translate, clipPath) is applied before first paint so there's no flash.
 */
export function SectionAnimator({ animation, sectionId, children }: SectionAnimatorProps) {
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !animation || animation.preset === "none") return;

    const ctx = gsap.context(() => {
      applyPreset(el, animation);
    }, el);

    return () => ctx.revert();
  }, [animation?.preset, animation?.scrub, animation?.start]);

  if (!animation || animation.preset === "none") {
    return <>{children}</>;
  }

  // parallax-bg animates an inner child, not the wrapper itself —
  // willChange on the wrapper would create an unnecessary stacking context
  const needsWillChange = animation.preset !== "parallax-bg";

  // sticky-exit requires overflow:hidden + border-radius so the scale-down
  // clips the edges cleanly (the CodePen card-stack visual)
  const isStickyExit = animation.preset === "sticky-exit";

  return (
    <div
      ref={ref}
      data-section-id={sectionId}
      style={{
        ...(needsWillChange ? { willChange: "transform, opacity" } : {}),
        ...(isStickyExit
          ? { overflow: "hidden", borderRadius: "var(--radius, 0.5rem)" }
          : {}),
      }}
    >
      {children}
    </div>
  );
}
